import React, { useState, useEffect } from 'react';
import uniqid from 'uniqid';
import classNames from 'classnames';
import type { SxProps, Theme } from '@mui/material/styles';

import Icons from 'assets/icons';

import { Box } from 'ui/box';

const ACTIVE_ITEM_SX: SxProps<Theme> = {
  bgcolor: 'tokens.navy.50',
  fontWeight: 600,
};

interface NavItem {
  id: string | number;
  navigationTitle?: string;
  type?: string;
  [key: string]: unknown;
}

interface ActiveItem {
  navigationTitle?: string;
  id?: string | number;
}

interface PageNavProps {
  content?: NavItem[];
  isTabletView?: boolean;
  navTitle?: string;
}

const PageNav = ({
  content = [],
  isTabletView = false,
  navTitle = 'In this article',
}: PageNavProps): React.JSX.Element | false => {
  const [activeItem, setActiveItem] = useState<ActiveItem>({});
  const [filteredContent, setFilteredContent] = useState<NavItem[]>([]);
  const [isNavBarOpen, setIsNavBarOpen] = useState<boolean>(false);

  const handleItemClick = ({
    navigationTitle,
    id,
  }: {
    navigationTitle?: string;
    id: string | number;
  }): void => {
    setActiveItem({ navigationTitle, id });

    const element = document.getElementById(`block_${id}`);
    const elPosition = element!.getBoundingClientRect();

    window.scrollTo(window.scrollX, elPosition.top - 112 + window.scrollY);

    setIsNavBarOpen(false);
  };

  useEffect(() => {
    const filteredParagraphs =
      content?.filter(
        (el) =>
          el.type === 'paragraph' &&
          el.navigationTitle &&
          !Object.is(el.navigationTitle, null)
      ) ?? [];

    setFilteredContent(filteredParagraphs);
    setActiveItem(filteredParagraphs[0] ?? {});
  }, []);

  return (
    !!filteredContent.length &&
    (!isTabletView ? (
      <Box
        sx={{
          '@media (min-width: 375px)': { mb: 3 },
          '@media (min-width: 960px)': { mb: 4 },
        }}
      >
        <Box sx={{ py: 1, color: 'tokens.navy.500', fontWeight: 600 }}>
          {navTitle}
        </Box>
        {filteredContent.map(({ navigationTitle, id }) => (
          <Box
            component="button"
            type="button"
            key={uniqid()}
            sx={[
              {
                width: '100%',
                textAlign: 'left',
                py: 1,
                px: 1.5,
                my: 0.25,
                cursor: 'pointer',
                borderRadius: '0.125rem',
              },
              activeItem.id === id && ACTIVE_ITEM_SX,
            ]}
            onClick={() => handleItemClick({ navigationTitle, id })}
          >
            {navigationTitle}
          </Box>
        ))}
      </Box>
    ) : (
      <Box
        sx={{
          position: 'relative',
          '@media (min-width: 375px)': { mb: 3 },
          '@media (min-width: 960px)': { mb: 4 },
        }}
      >
        <Box
          component="button"
          type="button"
          sx={[
            {
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1,
              px: 1.5,
              color: 'tokens.navy.500',
            },
            !isNavBarOpen && { borderRadius: '0.375rem' },
            isNavBarOpen && {
              borderTopRightRadius: '0.375rem',
              borderTopLeftRadius: '0.375rem',
              borderBottomWidth: 0,
            },
          ]}
          onClick={() => {
            setIsNavBarOpen(!isNavBarOpen);
          }}
        >
          <span>{navTitle}</span>
          <Icons.Arrow
            customClass={classNames('rotate-90', {
              '!rotate-[270deg]': isNavBarOpen,
            })}
          />
        </Box>
        {isNavBarOpen && (
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              bgcolor: 'common.white',
              borderWidth: '1px',
              borderColor: 'tokens.navy.200',
              cursor: 'pointer',
              borderBottomRightRadius: '0.375rem',
              borderBottomLeftRadius: '0.375rem',
            }}
          >
            {filteredContent.map(({ navigationTitle, id }, ind) => {
              const isLastItem = filteredContent.length === ind + 1;

              return (
                <Box
                  component="button"
                  type="button"
                  key={uniqid()}
                  sx={[
                    {
                      width: '100%',
                      textAlign: 'left',
                      py: 1,
                      px: 1.5,
                      cursor: 'pointer',
                    },
                    activeItem.id === id && ACTIVE_ITEM_SX,
                    isLastItem && {
                      borderBottomRightRadius: '0.375rem',
                      borderBottomLeftRadius: '0.375rem',
                    },
                  ]}
                  onClick={() => handleItemClick({ navigationTitle, id })}
                >
                  {navigationTitle}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    ))
  );
};

export default PageNav;
